import { Formik, type FormikHelpers } from 'formik';
import { sumBy } from 'lodash/fp';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { type ApplicationJourneyDepositFragment } from '../../../../api/fragments/ApplicationJourneyDeposit';
// eslint-disable-next-line max-len
import { type ConfiguratorApplicationEntrypointContextDataFragment } from '../../../../api/fragments/ConfiguratorApplicationEntrypointContextData';
import { type KycFieldSpecsFragment } from '../../../../api/fragments/KYCFieldSpecs';
import { CustomerKind, ModuleType } from '../../../../api/types';
import { type UploadDocumentProp } from '../../../../utilities/kycPresets';
import useKYCFormValidator from '../../../../utilities/kycPresets/useKYCValidators';
import useValidator from '../../../../utilities/useValidator';
import { type LocalVariant } from '../../../../utilities/useVehicleOptions';
import validators from '../../../../utilities/validators';
import { hasAppointmentScenario } from '../../../admin/ModuleDetailsPage/modules/implementations/shared';
import useAgreementsValidator from '../../../shared/CIPage/ConsentAndDeclarations/useAgreementsValidator';
import { useQuotationValidator } from '../../../shared/JourneyPage/QuotationDetails';
import { type State } from '../../StandardApplicationEntrypoint/Journey/reducer';
import { StyleContainer } from '../../StandardApplicationEntrypoint/styledComponents';
import { type ConfiguratorApplicationState } from '../Journey/JourneyController';
import KYCPageInner from './KYCPageInner';
import { type AgreementAndKycStateData, type Agreements, type ApplicantFormValues } from './types';

export type ApplicantFormProps = {
    configurator: ConfiguratorApplicationState;
    variants: LocalVariant[];
    kycPresets: KycFieldSpecsFragment[];
    applicationAgreements: Agreements;
    initialValues: ApplicantFormValues;
    onSubmit: (values: ApplicantFormValues, helpers: FormikHelpers<ApplicantFormValues>) => void;
    proceedWithCustomer: (values: ApplicantFormValues, helpers: FormikHelpers<ApplicantFormValues>) => void;
    state?: State<ConfiguratorApplicationState>;
    endpoint: ConfiguratorApplicationEntrypointContextDataFragment;
    deposit: ApplicationJourneyDepositFragment;
    setIsCorporate?: Dispatch<SetStateAction<boolean>>;
    customerKind: CustomerKind;
    isCorporate?: boolean;
    showTabs: boolean;
    hasGuarantorPreset: boolean;
    setPrefill: Dispatch<SetStateAction<boolean>>;
    prefill: boolean;
    setAgreementAndKyc?: Dispatch<SetStateAction<AgreementAndKycStateData>>;
    token?: string;
    shouldIncludeLayout?: boolean;
} & UploadDocumentProp;

const ApplicantForm = ({
    initialValues,
    kycPresets,
    applicationAgreements,
    state,
    onSubmit,
    customerKind,
    isCorporate,
    prefill,
    setAgreementAndKyc,
    token,
    uploadDocument,
    removeDocument,
    endpoint,
    ...props
}: ApplicantFormProps) => {
    const { application } = state;

    const { module } = application;

    if (module.__typename !== 'ConfiguratorModule') {
        return null;
    }
    const agreementValidators = useAgreementsValidator(applicationAgreements, 'agreements');
    const applicantsValidator = useKYCFormValidator(
        kycPresets,
        endpoint.configuratorApplicationModule.company.countryCode,
        'customer.fields'
    );

    const showUploadDocument = useMemo(
        () => application.configuration.withFinancing && application.bank?.hasUploadDocuments,
        [application.configuration.withFinancing, application.bank]
    );

    const showResetKYCButton = useMemo(() => {
        switch (application.module.__typename) {
            case ModuleType.ConfiguratorModule:
                return application.module.showResetKYCButton;

            default:
                return null;
        }
    }, [application.module]);

    const quotationValidator = useQuotationValidator(
        application.bank?.integration.__typename === 'EnbdBankIntegration' && application.configuration.withFinancing,
        sumBy('amount', application.financing?.dealerOptions ?? []),
        'quotation'
    );

    const validations = useMemo(
        () =>
            validators.compose(
                applicantsValidator,
                agreementValidators,
                quotationValidator,
                validators.only(
                    () => showUploadDocument && !isCorporate,
                    validators.requiredUploadFile(`uploadDocuments.${CustomerKind.Local}`)
                ),
                validators.only(
                    () => showUploadDocument && isCorporate,
                    validators.requiredUploadFile(`uploadDocuments.${CustomerKind.Corporate}`)
                ),
                validators.only(
                    () => showUploadDocument && customerKind === CustomerKind.Guarantor,
                    validators.requiredUploadFile(`uploadDocuments.${CustomerKind.Guarantor}`)
                ),
                validators.only(
                    (values, error, { prefix }) =>
                        customerKind !== CustomerKind.Guarantor &&
                        values.configuration.testDrive &&
                        hasAppointmentScenario(endpoint.configuratorApplicationModule.scenarios) &&
                        endpoint.configuratorApplicationModule.appointmentModule &&
                        endpoint.configuratorApplicationModule.displayAppointmentDatepicker &&
                        !values.appointment?.useCurrentDateTime,
                    validators.compose(
                        validators.requiredDate('appointment.date'),
                        validators.requiredString('appointment.time')
                    )
                ),
                validators.only(
                    values => !!values.configuration.isAffinAutoFinanceCentreRequired,
                    validators.compose(validators.requiredString('financing.affinAutoFinanceCentre'))
                )
            ),
        [
            agreementValidators,
            applicantsValidator,
            customerKind,
            isCorporate,
            quotationValidator,
            showUploadDocument,
            endpoint.configuratorApplicationModule,
        ]
    );
    const validate = useValidator(validations, { prefill });

    return (
        <StyleContainer>
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                <KYCPageInner
                    {...props}
                    applicationAgreements={applicationAgreements}
                    customerKind={customerKind}
                    endpoint={endpoint}
                    kycPresets={kycPresets}
                    removeDocument={removeDocument}
                    setAgreementAndKyc={setAgreementAndKyc}
                    showResetButton={showResetKYCButton}
                    state={state}
                    token={token}
                    uploadDocument={uploadDocument}
                />
            </Formik>
        </StyleContainer>
    );
};

export default ApplicantForm;
